import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, Target } from 'lucide-angular';

/**
 * Letter grade categories
 */
export type LetterGrade = 'A' | 'B' | 'C' | 'D' | 'F';

/**
 * Grade distribution entry for the chart
 */
export interface GradeDistributionEntry {
  grade: LetterGrade;
  count: number;
  percentage: number;
  colorClass: string;
  bgColorClass: string;
}

/**
 * Grade color metadata
 */
const GRADE_COLORS: Record<LetterGrade, { color: string; bg: string }> = {
  A: { color: 'text-emerald-700', bg: 'bg-emerald-500' },
  B: { color: 'text-green-700', bg: 'bg-green-500' },
  C: { color: 'text-yellow-700', bg: 'bg-yellow-500' },
  D: { color: 'text-orange-700', bg: 'bg-orange-500' },
  F: { color: 'text-red-700', bg: 'bg-red-500' },
};

/**
 * Grade color class for the average letter grade label
 */
const GRADE_LABEL_COLORS: Record<LetterGrade, string> = {
  A: 'text-emerald-600',
  B: 'text-green-600',
  C: 'text-yellow-600',
  D: 'text-orange-600',
  F: 'text-red-600',
};

/**
 * Convert a percentage to a letter grade
 */
export function percentageToLetterGrade(percentage: number): LetterGrade {
  if (percentage >= 90) return 'A';
  if (percentage >= 80) return 'B';
  if (percentage >= 70) return 'C';
  if (percentage >= 60) return 'D';
  return 'F';
}

/**
 * Get the color class for a letter grade label
 */
export function getGradeLabelColorClass(grade: LetterGrade): string {
  return GRADE_LABEL_COLORS[grade];
}

/**
 * Build grade distribution entries from an array of percentages.
 * Filters out null/undefined values.
 */
export function buildGradeDistribution(
  percentages: (number | null | undefined)[],
): GradeDistributionEntry[] {
  const validPercentages = percentages.filter(
    (p): p is number => p != null,
  );
  const total = validPercentages.length;

  if (total === 0) return [];

  const counts: Record<LetterGrade, number> = {
    A: 0,
    B: 0,
    C: 0,
    D: 0,
    F: 0,
  };

  for (const pct of validPercentages) {
    counts[percentageToLetterGrade(pct)]++;
  }

  const grades: LetterGrade[] = ['A', 'B', 'C', 'D', 'F'];

  return grades.map((grade) => ({
    grade,
    count: counts[grade],
    percentage: total > 0 ? (counts[grade] / total) * 100 : 0,
    colorClass: GRADE_COLORS[grade].color,
    bgColorClass: GRADE_COLORS[grade].bg,
  }));
}

/**
 * Build grade distribution entries from letter grades directly.
 */
export function buildGradeDistributionFromLetters(
  letterGrades: LetterGrade[],
): GradeDistributionEntry[] {
  const total = letterGrades.length;

  if (total === 0) return [];

  const counts: Record<LetterGrade, number> = {
    A: 0,
    B: 0,
    C: 0,
    D: 0,
    F: 0,
  };

  for (const grade of letterGrades) {
    counts[grade]++;
  }

  const grades: LetterGrade[] = ['A', 'B', 'C', 'D', 'F'];

  return grades.map((grade) => ({
    grade,
    count: counts[grade],
    percentage: total > 0 ? (counts[grade] / total) * 100 : 0,
    colorClass: GRADE_COLORS[grade].color,
    bgColorClass: GRADE_COLORS[grade].bg,
  }));
}

@Component({
  selector: 'app-grade-distribution-chart',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './grade-distribution-chart.html',
  styleUrl: './grade-distribution-chart.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GradeDistributionChartComponent {
  protected readonly icons = { Target };

  /** The grade distribution data to display */
  readonly distribution = input.required<GradeDistributionEntry[]>();

  /** Optional average letter grade to display above the chart */
  readonly averageGrade = input<LetterGrade | null>(null);

  /** Optional average grade color class */
  readonly averageGradeColorClass = input<string>('');
}
